import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import AlbumOutlinedIcon from '@mui/icons-material/AlbumOutlined';
import CallMergeOutlined from '@mui/icons-material/CallMergeOutlined';
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined';
import ChangeHistoryOutlinedIcon from '@mui/icons-material/ChangeHistoryOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ListOutlined from '@mui/icons-material/ListOutlined';
import { CircularProgress, ListItemIcon, ListItemText, MenuItem, Tooltip } from '@mui/material';

import type { Proposal } from 'api/projectVersioning/types';
import { ConditionalWrapper } from 'components/data-display/ConditionalWrapper';
import { RouterLink } from 'components/navigation/RouterLink';
import { IMPORT_IN_PROGRESS_TOOLTIP } from 'state/imports/importsInProgress';

import { getPathForTargetContext } from '../utils/getPathForTargetContext';
import StatusIcon from './StatusIcon';

type MenuItemBase = {
  id: string;
  icon?: React.ReactNode;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
};

type MenuActionItem = MenuItemBase & {
  onClick: () => void;
};

type MenuLinkItem = MenuItemBase & {
  to: string;
};

type MenuItemProps = MenuActionItem | MenuLinkItem;

type ProposalActionsProps = {
  isInProposal: boolean;
  onCreateProposal: () => void;
  onMergeProposal: () => void;
  onDiscardProposal: () => void;
  onSeeAllProposals: () => void;
  onReviewChanges: () => void;
  viewLiveUrl: string;
  proposals: Proposal[];
  disabled?: boolean;
  loading?: boolean;
};

export const ProposalMenuActions: React.FC<ProposalActionsProps> = ({
  isInProposal,
  onCreateProposal,
  onMergeProposal,
  onDiscardProposal,
  onSeeAllProposals,
  onReviewChanges,
  viewLiveUrl,
  proposals,
  disabled = false,
  loading = false
}) => {
  const isDisabled = disabled || loading;
  const loadingIcon = loading ? <CircularProgress size={16} /> : undefined;
  const disabledTooltip = loading ? undefined : IMPORT_IN_PROGRESS_TOOLTIP;
  const location = useLocation();
  const recentProposalsItems = useMemo(() => {
    return isInProposal
      ? []
      : [...(proposals ?? [])]
          .sort((a, b) => new Date(b.updated ?? b.created).getTime() - new Date(a.updated ?? a.created).getTime())
          .slice(0, 3)
          .map((proposal) => ({
            id: `view-proposal-${proposal.name}`,
            icon: <StatusIcon status={proposal.status} />,
            label: proposal.name,
            to: getPathForTargetContext({
              pathname: location.pathname,
              projectId: proposal.projectId,
              currentProposalId: undefined,
              targetProposalId: proposal.id
            }),
            show: !isInProposal
          }));
  }, [isInProposal, location.pathname, proposals]);

  const menuItems: MenuItemProps[] = isInProposal
    ? [
        {
          id: 'review-changes',
          icon: <ChangeHistoryOutlinedIcon />,
          label: 'Review changes',
          onClick: onReviewChanges
        },
        {
          id: 'merge-proposal',
          icon: loadingIcon ?? <CallMergeOutlined />,
          label: 'Merge proposal',
          onClick: onMergeProposal,
          disabled: isDisabled,
          disabledTooltip
        },
        {
          id: 'discard-proposal',
          icon: loadingIcon ?? <DeleteOutlinedIcon />,
          label: 'Discard proposal',
          onClick: onDiscardProposal,
          disabled: isDisabled,
          disabledTooltip
        },
        {
          id: 'view-live',
          icon: <AlbumOutlinedIcon />,
          label: 'View live',
          to: viewLiveUrl
        }
      ]
    : [
        {
          id: 'create-new-proposal',
          icon: <CallSplitOutlinedIcon />,
          label: 'Create new proposal',
          onClick: onCreateProposal
        },
        ...recentProposalsItems,
        {
          id: 'see-all-proposals',
          icon: <ListOutlined />,
          label: 'See all proposals',
          onClick: onSeeAllProposals
        }
      ];

  return (
    <>
      {menuItems.map((item) => {
        const commonProps = { disabled: item.disabled, 'data-testid': `proposal-action-${item.id}` };

        const menuItem =
          'to' in item ? (
            <MenuItem component={RouterLink} to={item.to} {...commonProps}>
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          ) : (
            <MenuItem onClick={item.onClick} {...commonProps}>
              {item.icon && <ListItemIcon>{item.icon}</ListItemIcon>}
              <ListItemText>{item.label}</ListItemText>
            </MenuItem>
          );

        return (
          <ConditionalWrapper
            key={item.id}
            condition={Boolean(item.disabled && item.disabledTooltip)}
            wrapper={(children) => (
              <Tooltip title={item.disabledTooltip}>
                <span>{children}</span>
              </Tooltip>
            )}
          >
            {menuItem}
          </ConditionalWrapper>
        );
      })}
    </>
  );
};
